import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';

import withReport from 'components/with-report';
import { ADD_ESTABLISHMENT_DETAILS_STEP } from 'constants/report-steps';
import { CONTRACTOR_REPORT_TYPE } from 'constants/common';
import { Establishment, Report } from 'graphql-api';
import { getEstablishments } from 'api/establishments';
import { navigateAndStoreNextStep } from 'utils/navigation';
import Spinner from 'components/spinner';
import StepProgressBar from 'components/step-progress-bar';
import EstablishmentForm from 'components/add-establishment-details-page/establishment-form';
import { updateReport } from 'api/reports';

function AddEstablishmentDetailsPage({
  report,
  setReport
}: {
  report: Report;
  setReport: Function;
}): JSX.Element {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [headQuarterError, setHeadQuarterError] = useState(false);
  const [establishments, setEstablishments] = useState<Establishment[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const currentEstablishment = establishments[currentIndex];
  const numberOfEstablishments = establishments.length;

  useEffect(() => {
    void (async () => {
      if (report !== null) {
        const data: Establishment[] = (await getEstablishments(report?.id)) as Establishment[];
        setEstablishments(data);
        setIsLoading(false);
      }
    })();
  }, [report]);

  const handleHeadQuarterFullAddressChange = async (
    event: React.ChangeEvent<HTMLSelectElement>
  ): Promise<void> => {
    const headQuarterFullAddress = event.target.value;

    if (!isEmpty(headQuarterFullAddress)) {
      const updatedReport = await updateReport(report, { headQuarterFullAddress });
      setReport(updatedReport);
      setHeadQuarterError(false);
    } else {
      setHeadQuarterError(true);
    }
  };

  const handlePrevious = (): void => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleNext = (updatedEstablishment: Establishment): void => {
    establishments[currentIndex] = updatedEstablishment;
    setEstablishments(establishments);
    if (isEmpty(report?.headQuarterFullAddress)) {
      return setHeadQuarterError(true);
    }
    if (currentIndex < numberOfEstablishments - 1) {
      setCurrentIndex(currentIndex + 1);
      window.scrollTo(0, 0);
    } else {
      void navigateAndStoreNextStep(report, ADD_ESTABLISHMENT_DETAILS_STEP, navigate);
    }
  };

  return (
    <div className="max-w-6xl m-auto bg-white shadow mt-8 py-16 px-14">
      <StepProgressBar report={report} currentStep={ADD_ESTABLISHMENT_DETAILS_STEP} />
      <div className="text-2xl font-medium leading-6 text-gray-900">Add establishment details</div>

      {isLoading ? (
        <div className="text-center py-16">
          <Spinner className="text-gray-500" size={48} />
        </div>
      ) : (
        <>
          {numberOfEstablishments > 0 && (
            <>
              <div className="text-base text-gray-700 mt-2 w-4/5">
                {report.reportType === CONTRACTOR_REPORT_TYPE
                  ? `Based on your uploaded data, we’ve determined there are ${numberOfEstablishments} unique establishments with 
                  labor contractors associated with your company. Select your company headquarters (or select "None of these"), then 
                  fill out the details for each establishment below.`
                  : `Based on your uploaded data, we’ve determined there are ${numberOfEstablishments} unique establishments associated
                  with your company. Select your company headquarters, then fill out the details for each establishment below.`}
              </div>

              <div className="mt-12">
                <label
                  htmlFor="head-quarter-full-address"
                  className="block text-sm font-medium text-gray-900">
                  Select Company Headquarters
                </label>
                <div className="mb-4 mt-2 text-sm text-gray-700">
                  Which of the following locations is legally designated as your company
                  headquarters?
                </div>
                <select
                  id="head-quarter-full-address"
                  defaultValue={report?.headQuarterFullAddress ?? ''}
                  onChange={(event) => {
                    void handleHeadQuarterFullAddressChange(event);
                  }}
                  className="select">
                  <option value="">– Select –</option>
                  {establishments.map((establishment, index) => (
                    <option key={index} value={establishment.fullAddress ?? ''}>
                      {establishment.fullAddress}
                    </option>
                  ))}
                  <option value="None">
                    {report.reportType !== CONTRACTOR_REPORT_TYPE
                      ? 'None of these (HQ is not CA based & has no CA employees)'
                      : 'None of these (HQ is not CA based or has no CA contractors)'}
                  </option>
                </select>
                {headQuarterError && (
                  <p className="error-message">
                    Please select your company headquarters to continue
                  </p>
                )}
              </div>
              {currentEstablishment !== null && (
                <EstablishmentForm
                  key={`establishment-form-${currentIndex}`}
                  establishment={currentEstablishment}
                  index={currentIndex}
                  numberOfEstablishments={numberOfEstablishments}
                  handlePrevious={handlePrevious}
                  handleNext={handleNext}
                />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

export default withReport(AddEstablishmentDetailsPage);
